import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { ImageMetadata, imageApi } from '../../services/imageApi';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import {
  X, ZoomIn, ZoomOut, RotateCw, Download, AlertCircle, Loader2, FileText
} from 'lucide-react';
import { formatDateTime } from '../../utils/dateFormat';

interface ImageViewerProps {
  image: ImageMetadata;
  caseId: string;
  onClose: () => void;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({ image, caseId, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const { openDocument } = useDocumentViewer();

  // Resolve source document name from documents cache
  const { data: documentsData } = useQuery<any>(
    ['documents', caseId],
    { enabled: false }
  );
  const sourceDocumentName = useMemo(() => {
    if (!documentsData || !image.documentId) return null;
    const docs = Array.isArray(documentsData) ? documentsData : documentsData?.documents || [];
    const doc = docs.find((d: any) => d.documentId === image.documentId);
    return doc?.filename || null;
  }, [documentsData, image.documentId]);

  const handleViewSourceDocument = useCallback(() => {
    if (!image.documentId) return;
    openDocument({
      documentId: image.documentId,
      documentName: sourceDocumentName || 'Source Document',
      pageNumber: image.pageNumber,
    });
  }, [image.documentId, image.pageNumber, sourceDocumentName, openDocument]);

  // Load image via authenticated fetch
  const { blobUrl, loading: imageLoading, containerRef: imageContainerRef } = useAuthenticatedImage(image.url);

  // Authenticated download handler
  const handleDownload = useCallback(async () => {
    try {
      const blob = await imageApi.fetchImageBlob(image.url);
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = image.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);
    } catch {
      console.error('Failed to download image');
    }
  }, [image.url, image.filename]);

  const handleZoomIn = () => setZoom(Math.min(zoom + 0.25, 3));
  const handleZoomOut = () => setZoom(Math.max(zoom - 0.25, 0.5));
  const handleRotate = () => setRotation((rotation + 90) % 360);

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
        <h3 className="text-white font-medium truncate max-w-md" title={image.filename}>
          {image.filename}
        </h3>

        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-gray-300 text-sm min-w-[4rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-5 h-5" />
          </button>

          <button
            onClick={handleRotate}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Rotate"
          >
            <RotateCw className="w-5 h-5" />
          </button>

          <button
            onClick={handleDownload}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Download"
          >
            <Download className="w-5 h-5" />
          </button>

          <button
            onClick={onClose}
            className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image Display Area */}
      <div ref={imageContainerRef} className="flex-1 overflow-auto flex items-center justify-center p-4">
        <div className="relative inline-block">
          {imageLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <img
              src={blobUrl || ''}
              alt={image.filename}
              className="max-w-full max-h-full"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: 'center',
                transition: 'transform 0.2s',
              }}
            />
          )}
        </div>
      </div>

      {/* Image Details */}
      <div className="bg-gray-800 border-t border-gray-700 p-4 max-h-64 overflow-y-auto">
        <h4 className="text-white font-medium mb-3">Image Details</h4>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Format:</span>
            <span className="text-gray-200">{image.format}</span>
          </div>
          {image.dimensions && (
            <div className="flex justify-between">
              <span className="text-gray-400">Dimensions:</span>
              <span className="text-gray-200">
                {image.dimensions.width} × {image.dimensions.height}
              </span>
            </div>
          )}
          {image.pageNumber && (
            <div className="flex justify-between">
              <span className="text-gray-400">Page:</span>
              <span className="text-gray-200">{image.pageNumber}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Extraction:</span>
            <span className="text-gray-200 capitalize">{image.extractionMethod}</span>
          </div>
        </div>

        {image.documentId && (
          <button
            onClick={handleViewSourceDocument}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            View Source Document
            {image.pageNumber ? ` (p.${image.pageNumber})` : ''}
          </button>
        )}

        {image.aiAnalysis && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div>
                <h5 className="text-white font-medium text-sm">AI Observation</h5>
                <p className="text-xs text-gray-400 mt-1">
                  Descriptive observation from document processing. Not a medical diagnosis.
                </p>
              </div>
            </div>
            <p className="text-gray-300 text-sm mt-2 leading-relaxed">
              {image.aiAnalysis}
            </p>
            {image.aiAnalysisTimestamp && (
              <p className="text-xs text-gray-500 mt-2">
                Generated: {formatDateTime(image.aiAnalysisTimestamp)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
